import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import {
  getApiErrorDetail,
  UNIQUENESS_CHECK_DEBOUNCE_MS,
  useApiClient,
  useDebouncedValue,
} from '@klareospace/pharos-lib-front-utils';
import {
  buildOptionalPhoneNumberSchema,
  buildPhoneNumberSchema,
} from '@klareospace/pharos-lib-front-utils/phone-validation';
import { DEFAULT_DIAL_CODE, SITE_TYPE } from '@klareospace/pharos-lib-types';

import type { CreateSiteFormInput } from '../schemas/create-site.schema';
import { createSiteApi, siteKeys } from '../api';
import { buildSiteLocation, hasPhoneData } from '../utils/site-form.utils';
import { useCreateSite } from './use-create-site';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { CreateSiteFormInput };

export interface UseCreateSiteFormOptions {
  defaultSpaceId?: string;
  onSuccess: () => void;
}

export interface UseCreateSiteFormReturn {
  form: UseFormReturn<CreateSiteFormInput>;
  isPending: boolean;
  onSubmit: (data: CreateSiteFormInput) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCreateSiteForm({ defaultSpaceId, onSuccess }: UseCreateSiteFormOptions): UseCreateSiteFormReturn {
  const { t } = useTranslation('settings');
  const createSite = useCreateSite();
  const client = useApiClient('admin-auth');
  const api = createSiteApi(client);

  const schema = useMemo(
    () =>
      z.object({
        address: z.object({
          additionalInformations: z.string().optional(),
          city: z.string().trim().min(1, t('sites.create.validation.cityRequired')),
          complement: z.string().optional(),
          country: z.string().trim().min(1, t('sites.create.validation.countryRequired')),
          digicode: z.string().optional(),
          floor: z.string().optional(),
          hasElevator: z.boolean().optional(),
          houseNumber: z.string().optional(),
          postalCode: z.string().trim().min(1, t('sites.create.validation.postalCodeRequired')),
          street: z.string().trim().optional(),
        }),
        code: z.string().trim().min(1, t('sites.create.validation.codeRequired')),
        color: z.string().min(1, t('sites.create.validation.colorRequired')),
        coordinates: z
          .object({
            latitude: z.number().optional(),
            longitude: z.number().optional(),
          })
          .optional(),
        customerSupportPhone: buildOptionalPhoneNumberSchema({ invalid: t('sites.create.validation.phoneInvalid') }),
        geocodingScore: z.number().min(1, t('sites.create.validation.geocodingRequired')),
        geocodingSource: z.enum(['autocomplete', 'manual']).optional(),
        name: z.string().trim().min(1, t('sites.create.validation.nameRequired')),
        phone: buildPhoneNumberSchema({
          invalid: t('sites.create.validation.phoneInvalid'),
          required: t('sites.create.validation.phoneRequired'),
        }),
        spaceId: z.string().min(1, t('sites.create.validation.spaceRequired')),
        type: z.enum(SITE_TYPE, {
          error: t('sites.create.validation.typeRequired'),
        }),
      }),
    [t],
  );

  const form = useForm<CreateSiteFormInput>({
    defaultValues: {
      address: {
        additionalInformations: '',
        city: '',
        complement: '',
        country: '',
        digicode: '',
        floor: '',
        hasElevator: false,
        houseNumber: '',
        postalCode: '',
        street: '',
      },
      code: '',
      color: '#64C466',
      coordinates: {
        latitude: undefined,
        longitude: undefined,
      },
      customerSupportPhone: null,
      geocodingScore: undefined,
      geocodingSource: undefined,
      name: '',
      phone: { dialCode: DEFAULT_DIAL_CODE, number: '' },
      spaceId: defaultSpaceId ?? '',
      type: SITE_TYPE.SHOP,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  // ─── Debounced code uniqueness check ──────────────────────────────────────

  const codeValue = form.watch('code');
  const codeError = form.formState.errors.code;
  const debouncedCode = useDebouncedValue(codeValue, UNIQUENESS_CHECK_DEBOUNCE_MS);

  const { data: codeCheckData } = useQuery({
    enabled: !!debouncedCode && debouncedCode.length >= 1 && codeError?.type !== 'manual',
    gcTime: 60_000,
    queryFn: () => api.checkCodeExists(debouncedCode),
    queryKey: siteKeys.checkCode(debouncedCode),
    staleTime: 30_000,
  });

  const { setError } = form;
  useEffect(() => {
    if (codeCheckData === true) {
      setError('code', { message: t('sites.create.validation.codeExists'), type: 'manual' });
    }
  }, [codeCheckData, setError, t]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  function onSubmit(data: CreateSiteFormInput): void {
    if (createSite.isPending) return;

    const location = buildSiteLocation({
      address: data.address,
      coordinates: data.coordinates ?? {},
      source: data.geocodingSource ?? 'manual',
    });

    createSite
      .mutateAsync({
        code: data.code,
        color: data.color,
        ...(hasPhoneData(data.customerSupportPhone) ? { customerSupportPhone: data.customerSupportPhone } : {}),
        ...(location !== undefined ? { location } : {}),
        name: data.name,
        phone: data.phone,
        spaceId: data.spaceId,
        type: data.type,
      })
      .then(
        () => {
          toast.success(t('sites.create.success'));
          onSuccess();
        },
        (error: unknown) => {
          const detail = getApiErrorDetail(error);
          if (detail.statusCode === 409) {
            const message = detail.message ?? '';
            if (message.includes('code')) {
              form.setError('code', { message: t('sites.create.validation.codeExists') });
            } else {
              form.setError('name', { message: t('sites.create.validation.nameExists') });
            }
            return;
          }
          toast.error(detail.message || t('sites.create.error'));
        },
      );
  }

  return {
    form,
    isPending: createSite.isPending,
    onSubmit,
  };
}
